/**
 * Header Component
 *
 * Top navigation bar with mobile menu toggle, theme switch, and user dropdown.
 */

import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Moon, Sun, User, LogOut, Settings, ChevronDown } from 'lucide-react';

import { useAuth, useTheme } from '@/hooks';
import { cn } from '@/utils/cn';

interface HeaderProps {
  /** Open mobile menu */
  onMobileMenuOpen: () => void;
}

export function Header({ onMobileMenuOpen }: HeaderProps) {
  const { user, logout } = useAuth();
  const { toggleTheme, isDark } = useTheme();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setDropdownOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  return (
    <header className="flex h-16 items-center justify-between px-4 border-b border-border dark:border-border-dark bg-card dark:bg-card-dark">
      {/* Left side - Mobile menu button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onMobileMenuOpen}
          className="lg:hidden flex items-center justify-center h-9 w-9 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5 text-foreground-muted" />
        </button>
      </div>

      {/* Right side - Actions */}
      <div className="flex items-center gap-2">
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          className="flex items-center justify-center h-9 w-9 rounded-md hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
          title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDark ? (
            <Sun className="h-5 w-5 text-foreground-muted" />
          ) : (
            <Moon className="h-5 w-5 text-foreground-muted" />
          )}
        </button>

        {/* User dropdown */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            className={cn(
              'flex items-center gap-2 px-2 py-1.5 rounded-md',
              'hover:bg-secondary-100 dark:hover:bg-secondary-800',
              'transition-colors',
              dropdownOpen && 'bg-secondary-100 dark:bg-secondary-800'
            )}
            aria-expanded={dropdownOpen}
            aria-haspopup="true"
          >
            {/* Avatar */}
            <div className="h-8 w-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <User className="h-4 w-4 text-primary" />
            </div>

            {/* User info - hidden on small screens */}
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-foreground dark:text-foreground-dark truncate max-w-[120px]">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-foreground-muted dark:text-foreground-dark-muted">
                {user?.orgUserType}
              </p>
            </div>

            <ChevronDown
              className={cn(
                'h-4 w-4 text-foreground-muted transition-transform',
                dropdownOpen && 'rotate-180'
              )}
            />
          </button>

          {/* Dropdown menu */}
          {dropdownOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-lg border border-border dark:border-border-dark bg-card dark:bg-card-dark shadow-lg py-1 z-50 animate-fade-in">
              {/* User info */}
              <div className="px-4 py-3 border-b border-border dark:border-border-dark">
                <p className="text-sm font-medium text-foreground dark:text-foreground-dark truncate">
                  {user?.firstName} {user?.lastName}
                </p>
                <p className="text-xs text-foreground-muted dark:text-foreground-dark-muted truncate">
                  {user?.email}
                </p>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <DropdownItem
                  to="/profile"
                  icon={<User className="h-4 w-4" />}
                  label="Profile"
                  onClick={() => setDropdownOpen(false)}
                />

                <DropdownItem
                  to="/settings"
                  icon={<Settings className="h-4 w-4" />}
                  label="Settings"
                  onClick={() => setDropdownOpen(false)}
                />
              </div>

              {/* Logout */}
              <div className="border-t border-border dark:border-border-dark py-1">
                <button
                  onClick={() => {
                    setDropdownOpen(false);
                    logout();
                  }}
                  className="flex items-center gap-3 w-full px-4 py-2 text-sm text-danger hover:bg-danger-50 dark:hover:bg-danger-900/20"
                >
                  <LogOut className="h-4 w-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}

/**
 * Dropdown menu item
 */
interface DropdownItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

function DropdownItem({ to, icon, label, onClick }: DropdownItemProps) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="flex items-center gap-3 px-4 py-2 text-sm text-foreground dark:text-foreground-dark hover:bg-secondary-100 dark:hover:bg-secondary-800"
    >
      <span className="text-foreground-muted">{icon}</span>
      {label}
    </Link>
  );
}
